import React, {
  useState,
} from 'react';
import { useTheme } from 'react-jss';
import PropTypes from 'prop-types';
import InputBase from '@/components/InputBase';
import InputMask from '@/components/InputMask';
import cx from '@/utils/cx';
import useStyle from './style';
import ClearIcon from '@/assets/close.svg';

const GeneralInput = ({
  validation,
  formatter,
  value,
  onChange,
  onEnter,
  onFocus,
  onBlur,
  error,
  showClear,
  showDot,
  label,
  placeholder,
  floatingPlaceholder,
  type,
  _size = 'md',
  _text_align,
  _dir,
  _border,
  maxLength,
  showLengthCounter,
  children,
  inputProps,
  placeholderProps,
  className,
  required,
  clearId,
  ...rest
}) => {
  const theme = useTheme();
  const [focus, setFocus] = useState(false);
  const currentTimestamp = Date.now();

  const localDir = _dir || theme.direction.start;

  const classes = useStyle({ _dir: localDir, _size });

  const handleFocus = (event) => {
    setFocus(true);
    onFocus(event);
  };

  const handleBlur = (event) => {
    setFocus(false);
    onBlur(event);
  };

  const handleOnChange = (e) => {
    onChange(type === 'mask' ? e : e.target.value);
  };

  const handleEnter = (event) => {
    if (event.type === 'keyup' && event.keyCode !== 13) {
      return;
    }
    onEnter();
  };

  const handleClear = () => {
    onChange(type === 'mask' ? [] : '');
  };

  // ui sections
  const Required = required && (
    <span className="required">*</span>
  );

  const Label = label && (
    <label htmlFor={inputProps.id || `input-${currentTimestamp}`} className="label" _text_size="md">
      {label}
      {!floatingPlaceholder && Required}
    </label>
  );

  const Counter = typeof maxLength === 'number' && showLengthCounter && (
    <div className="counter" _text_size="sm" _text_color={(value ? value.length : 0) >= maxLength ? 'danger' : 'gray'} _padding_top="md" _text_align="end">
      {`${value ? value.length : 0} / ${maxLength}`}
    </div>
  );

  const Input = (() => {
    let Component;
    let tagName;
    const componentProps = {
      ...inputProps,
      type,
      _text_align,
      onKeyUp: handleEnter,
      onClick: handleEnter,
      onBlur: handleBlur,
      onFocus: handleFocus,
    };
    if (type === 'custom') {
      tagName = 'div';
      Component = 'div';
      componentProps.tabIndex = 0;
      componentProps.children = value;
    } else if (type === 'textarea' || type === 'multiline') {
      tagName = 'textarea';
      Component = 'textarea';
    } else if (type === 'mask') {
      tagName = 'mask';
      Component = InputMask;
    } else {
      tagName = 'input';
      Component = InputBase;
      componentProps.maxLength = maxLength;
    }
    if (type !== 'custom') {
      componentProps.value = value;
      componentProps.onChange = handleOnChange;
    }
    componentProps.className = cx('inner-tag', tagName);
    return <Component {...componentProps} id={inputProps.id || `input-${currentTimestamp}`} />;
  })();

  const Children = children && (
    <div className="children">
      {children}
    </div>
  );

  const Placeholder = (
    <div className="placeholder" _text_align={_text_align} {...placeholderProps}>
      {placeholder}
      {floatingPlaceholder && Required}
    </div>
  );

  const Controls = (showDot || showClear) && (
    <div className="controls">
      {showDot && (
        <span
          className="icons dot-icon" />
      )}
      {showClear && (
        <ClearIcon
          width="16"
          height="16"
          viewBox="0 0 24 24"
          className="icons clear-icon"
          id={clearId}
          onMouseDown={handleClear}
          onTouchStart={handleClear} />
      )}
    </div>
  );

  return (
    <div
      className={cx(
        classes.generalInput,
        `error-${error}`,
        focus && 'focus',
        `type-${type}`,
        placeholder && 'has-placeholder',
        !value || (type === 'mask' && value.join('').length === 0) ? 'empty' : '',
        !children && 'no-children',
        floatingPlaceholder && 'floating-placeholder',
        !_border && 'default-border',
        className,
      )}
      _dir={localDir}
      {...rest}
    >
      {Label}
      <div
        className="inner"
        _size={_size}
      >
        {Children}
        <div className="input-container">
          {Input}
          {Placeholder}
          {Controls}
        </div>
      </div>
      {Counter}
    </div>
  );
};

GeneralInput.propTypes = {
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  type: PropTypes.string,
  error: PropTypes.oneOf([true, false, null]),
  showClear: PropTypes.bool,
  showDot: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  floatingPlaceholder: PropTypes.bool,
  maxLength: PropTypes.number,
  showLengthCounter: PropTypes.bool,
  inputProps: PropTypes.object,
  placeholderProps: PropTypes.object,
  clearId: PropTypes.string,
};

const noop = () => { };

GeneralInput.defaultProps = {
  onChange: noop,
  onEnter: noop,
  onFocus: noop,
  onBlur: noop,
  type: 'text',
  floatingPlaceholder: false,
  error: null,
  showClear: true,
  showDot: true,
  label: '',
  placeholder: '',
  maxLength: undefined,
  showLengthCounter: true,
  inputProps: {},
  placeholderProps: {},
  clearId: '',
};

export default GeneralInput;
